import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, downloadFile } from "@/lib/api";
import { TaalKeuzeDialog, useTaalActie } from "@/components/TaalKeuzeDialog";
import { useDetailNav } from "@/lib/overzichtNav";
import { fmtEUR, fmtEURSigned, fmtDate, fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Plus, Trash2, Pencil, FileDown, Mail, ExternalLink, AlertTriangle, CheckCircle2, RotateCcw, FileMinus, Building2, ChevronLeft, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { BtwOverzicht } from "@/components/BtwOverzicht";
import { PakketStatusBadge, usePakketSync } from "@/components/PakketStatusBadge";
import { Switch } from "@/components/ui/switch";
import { ConfirmDeleteDialog } from "@/components/ConfirmDelete";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";

const toDateInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toISOString().slice(0, 10);};

function MoneybirdKnop({ factuur, onSynced }) {
  const [busy, setBusy] = useState(false);
  const mb = factuur?.moneybird || {};
  const isCredit = factuur?.type === "creditfactuur";
  const gesynchr = !!(mb.external_sales_invoice_id || mb.sales_invoice_id);

  const push = async () => {
    setBusy(true);
    try {
      const url = isCredit
        ? `/moneybird/creditfacturen/${factuur.id}/push?verstuur=false`
        : `/moneybird/facturen/${factuur.id}/push?verstuur=false`;
      const { data } = await api.post(url);
      toast.success(data?.status === "reeds_gesynchroniseerd"
        ? "Stond al in Moneybird" : "Verstuurd naar Moneybird");
      const { data: verse } = await api.get(`/verkoopfacturen/${factuur.id}`);
      onSynced?.(verse.moneybird || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Moneybird-push mislukt");
    } finally { setBusy(false); }
  };

  return (
    <Button variant={gesynchr ? "outline" : "outline"} size="sm" onClick={push} disabled={busy}
      className={gesynchr ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50" : ""}
      data-testid="factuur-moneybird-push"
      title={gesynchr ? `Moneybird ID ${mb.external_sales_invoice_id || mb.sales_invoice_id}${mb.invoice_id ? " · " + mb.invoice_id : ""}` : "Naar Moneybird sturen"}>
      <Building2 className="h-4 w-4 mr-1" />
      {busy ? "Bezig…" : (gesynchr ? "In Moneybird" : "Naar Moneybird")}
    </Button>
  );
}

function RegelDialog({ parentId, value, onSaved, trigger }) {
  const empty = { code: "", product: "", aantal: 1, inkoopprijs: 0, verkoopprijs: 0 };
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(value || empty);
  const [producten, setProducten] = useState([]);
  useEffect(() => { if (open) { setF(value || empty); api.get("/producten").then(r => setProducten(r.data)); } /* eslint-disable-next-line */ }, [open]);

  const pickProduct = (sku) => {
    const p = producten.find(x => x.sku === sku);
    if (!p) return;
    setF({ ...f, code: p.sku, product: p.naam,
           inkoopprijs: p.inkoopprijs || 0, verkoopprijs: p.verkoopprijs || 0 });
  };

  const submit = async () => {
    if (!f.product.trim()) { toast.error("Product is verplicht"); return; }
    try {
      const num = (v) => parseFloat(String(v).toString().replace(",", ".")) || 0;
      const payload = { ...f, aantal: num(f.aantal), inkoopprijs: num(f.inkoopprijs), verkoopprijs: num(f.verkoopprijs) };
      if (value?.id) await api.patch(`/verkoopfactuurregels/${value.id}`, payload);
      else await api.post("/verkoopfactuurregels", { ...payload, factuur_id: parentId });
      toast.success("Regel opgeslagen"); setOpen(false); onSaved();
    } catch { toast.error("Opslaan mislukt"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{value ? "Regel bewerken" : "Nieuwe regel"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Artikel uit catalogus</Label>
            <Select value="" onValueChange={pickProduct}>
              <SelectTrigger data-testid="freg-product-picker"><SelectValue placeholder="Kies bestaand product…" /></SelectTrigger>
              <SelectContent className="max-h-80">
                {producten.map(p => <SelectItem key={p.id} value={p.sku}>{p.sku} — {p.naam}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Code</Label><Input data-testid="freg-code" value={f.code || ""} onChange={(e)=>setF({...f,code:e.target.value})} /></div>
            <div className="col-span-2"><Label>Product</Label><Input data-testid="freg-product" value={f.product || ""} onChange={(e)=>setF({...f,product:e.target.value})} /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Aantal</Label><Input type="number" step="0.01" value={f.aantal} onChange={(e)=>setF({...f,aantal:e.target.value})} /></div>
            <div><Label>Inkoop (€)</Label><Input type="number" step="0.01" value={f.inkoopprijs} onChange={(e)=>setF({...f,inkoopprijs:e.target.value})} /></div>
            <div><Label>Verkoop (€)</Label><Input type="number" step="0.01" data-testid="freg-verkoop" value={f.verkoopprijs} onChange={(e)=>setF({...f,verkoopprijs:e.target.value})} /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={()=>setOpen(false)}>Annuleren</Button>
          <Button onClick={submit} data-testid="freg-submit">Opslaan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VerstuurDialog({ factuur, open, onOpenChange, onSent }) {
  const [factuurnummer, setFactuurnummer] = useState("");
  const [vervaldatum, setVervaldatum] = useState("");
  useEffect(() => {
    if (open && factuur) {
      setFactuurnummer(factuur.factuurnummer || "");
      const d = new Date(); d.setDate(d.getDate() + 30);
      setVervaldatum(d.toISOString().slice(0, 10));
    }
  }, [open, factuur]);

  const verzend = async () => {
    try {
      const { data } = await api.post(`/verkoopfacturen/${factuur.id}/verstuur-email`, {
        factuurnummer,
        vervaldatum: vervaldatum ? new Date(vervaldatum).toISOString() : null,
      });
      toast.success(`Factuur ${data.factuurnummer} verstuurd naar ${data.naar}`);
      onSent(); onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Versturen mislukt");
    }
  };
  if (!factuur) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Factuur versturen per e-mail</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-sm bg-secondary/50 rounded-md p-3 space-y-1">
            <div><span className="text-muted-foreground">Bedrag:</span> <strong>{fmtEURSigned(factuur.bedrag, factuur.type === "creditfactuur")}</strong></div>
            {factuur.ordernummer && <div><span className="text-muted-foreground">Ordernummer:</span> {factuur.ordernummer}</div>}
            {factuur.leveranciers_id && <div><span className="text-muted-foreground">Leveranciers-ID:</span> {factuur.leveranciers_id}</div>}
          </div>
          <div><Label>Factuurnummer</Label>
            <Input data-testid="verstuur-nummer" value={factuurnummer} onChange={(e)=>setFactuurnummer(e.target.value)} placeholder="Bijv. 2026-0042" />
            <div className="text-xs text-muted-foreground mt-1">Leeg laten → automatisch gegenereerd</div>
          </div>
          <div><Label>Vervaldatum</Label>
            <Input type="date" data-testid="verstuur-vervaldatum" value={vervaldatum} onChange={(e)=>setVervaldatum(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annuleren</Button>
          <Button onClick={verzend} data-testid="verstuur-submit">
            <Mail className="h-4 w-4 mr-2" /> Verstuur via e-mail
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CrediteerDialog({ factuur, regels, open, onOpenChange, onCreated }) {
  const [datum, setDatum] = useState("");
  const [reden, setReden] = useState("");
  const [items, setItems] = useState([]);
  const [bezig, setBezig] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDatum(new Date().toISOString().slice(0, 10));
    setReden("");
    setItems(regels.map(r => ({
      original_regel_id: r.id,
      code: r.code || "",
      product: r.product || "",
      aantal: Number(r.aantal) || 0,
      verkoopprijs: Number(r.verkoopprijs) || 0,
      inkoopprijs: Number(r.inkoopprijs) || 0,
      selected: true,
    })));
  }, [open, regels]);

  const toggle = (i) => setItems(prev => prev.map((it, idx) => idx === i ? { ...it, selected: !it.selected } : it));
  const setVeld = (i, k, v) =>
    setItems(prev => prev.map((it, idx) => idx === i ? { ...it, [k]: v } : it));

  const gekozen = items.filter(it => it.selected);
  const totaal = gekozen.reduce((s, it) => s + (Number(it.aantal) || 0) * (Number(it.verkoopprijs) || 0), 0);

  const submit = async () => {
    const payload = {
      datum: new Date(datum).toISOString(),
      reden: reden.trim(),
      regels: gekozen.map(it => ({
        original_regel_id: it.original_regel_id,
        code: it.code, product: it.product,
        aantal: Number(it.aantal) || 0,
        verkoopprijs: Number(it.verkoopprijs) || 0,
        inkoopprijs: Number(it.inkoopprijs) || 0,
      })).filter(r => r.aantal > 0 && (r.product || r.code)),
    };
    if (!payload.regels.length) {
      toast.error("Selecteer minimaal één regel met een aantal.");
      return;
    }
    setBezig(true);
    try {
      const { data } = await api.post(`/verkoopfacturen/${factuur.id}/crediteer`, payload);
      toast.success(`Creditfactuur ${data.factuurnummer} aangemaakt`);
      onOpenChange(false);
      onCreated(data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aanmaken mislukt");
    } finally {
      setBezig(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="crediteer-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-purple-700">
            <AlertTriangle className="h-5 w-5" /> Creditfactuur aanmaken
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-md border border-purple-200 bg-purple-50 p-3 text-sm text-purple-900">
            <div className="font-medium">Let op: dit maakt een nieuwe creditfactuur aan op basis van
              factuur <strong>{factuur.factuurnummer || "(concept)"}</strong>.</div>
            <div className="mt-1 text-purple-800">
              Pas hieronder de aantallen aan (bijv. 2 i.p.v. 12) om gedeeltelijk te crediteren. De
              originele factuur blijft ongewijzigd; de creditfactuur wordt als concept opgeslagen.
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Creditfactuurdatum</Label>
              <Input type="date" value={datum} onChange={(e) => setDatum(e.target.value)} data-testid="crediteer-datum" />
            </div>
            <div>
              <Label>Reden</Label>
              <Input value={reden} onChange={(e) => setReden(e.target.value)} placeholder="Bijv. Retour beschadigde verpakking" data-testid="crediteer-reden" />
            </div>
          </div>

          <div className="border border-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-secondary/40 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-3 py-2 w-8"></th>
                  <th className="text-left px-3 py-2">Code</th>
                  <th className="text-left px-3 py-2">Product</th>
                  <th className="text-right px-3 py-2 w-24">Aantal</th>
                  <th className="text-right px-3 py-2 w-28">Prijs (€)</th>
                  <th className="text-right px-3 py-2 w-28">Regel-totaal</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => {
                  const rt = (Number(it.aantal) || 0) * (Number(it.verkoopprijs) || 0);
                  return (
                    <tr key={it.original_regel_id || i} className={it.selected ? "" : "opacity-50"} data-testid={`crediteer-row-${i}`}>
                      <td className="px-3 py-2">
                        <Checkbox checked={it.selected} onCheckedChange={() => toggle(i)} data-testid={`crediteer-select-${i}`} />
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">{it.code}</td>
                      <td className="px-3 py-2">{it.product}</td>
                      <td className="px-3 py-2 text-right">
                        <Input className="text-right h-8" type="number" step="0.01"
                          value={it.aantal} onChange={(e) => setVeld(i, "aantal", e.target.value)}
                          disabled={!it.selected} data-testid={`crediteer-aantal-${i}`} />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Input className="text-right h-8" type="number" step="0.01"
                          value={it.verkoopprijs} onChange={(e) => setVeld(i, "verkoopprijs", e.target.value)}
                          disabled={!it.selected} />
                      </td>
                      <td className="px-3 py-2 text-right font-medium">-{fmtEUR(rt)}</td>
                    </tr>
                  );
                })}
                {items.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-6 text-muted-foreground">Geen regels om te crediteren.</td></tr>
                )}
              </tbody>
              <tfoot className="bg-secondary/20 border-t border-border">
                <tr>
                  <td colSpan={5} className="px-3 py-2 text-right font-medium">Totaal te crediteren</td>
                  <td className="px-3 py-2 text-right font-display text-lg font-medium text-purple-700" data-testid="crediteer-totaal">-{fmtEUR(totaal)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={bezig}>Annuleren</Button>
          <Button onClick={submit} disabled={bezig || gekozen.length === 0}
            className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="crediteer-submit">
            <FileMinus className="h-4 w-4 mr-2" /> {bezig ? "Aanmaken…" : "Creditfactuur aanmaken"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function VerkoopfactuurDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.rol === "admin";

  const [factuur, setFactuur] = useState(null);
  const [org, setOrg] = useState(null);
  const [regels, setRegels] = useState([]);
  const [btw, setBtw] = useState(null);
  const [verwantePakbonnen, setVerwantePakbonnen] = useState([]);
  const [verstuurOpen, setVerstuurOpen] = useState(false);
  const [mailing, setMailing] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [creditOpen, setCreditOpen] = useState(false);
  const taalKeuze = useTaalActie();

  const emailFactuur = async (taal) => {
    setMailing(true);
    try {
      const q = taal ? `?taal=${taal}` : "";
      const { data } = await api.post(`/verkoopfacturen/${id}/verstuur-email${q}`);
      toast.success(`Factuur ${data.factuurnummer} gemaild naar ${data.naar}`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "E-mailen mislukt");
    } finally { setMailing(false); }
  };

  const reload = async () => {
    let f;
    try {
      const { data } = await api.get(`/verkoopfacturen/${id}`);
      f = data;
    } catch {
      toast.error("Niet gevonden"); navigate("/verkoopfacturen"); return;
    }
    setFactuur(f);
    try {
      const { data: o } = await api.get(`/organisaties/${f.relatie_id}`);
      setOrg(o || null);
    } catch { setOrg(null); }
    const { data: r } = await api.get(`/verkoopfacturen/${id}/regels`);
    setRegels(r);
    try { const { data: b } = await api.get(`/verkoopfacturen/${id}/btw`); setBtw(b); } catch (e) { console.warn("BTW ophalen mislukt:", e); }
    // Haal verwante pakbon(nen) op zodat we de MyParcel-status kunnen tonen
    if (f.verkoopkans_id) {
      try {
        const { data: pbs } = await api.get("/pakbonnen", { params: { verkoopkans_id: f.verkoopkans_id } });
        setVerwantePakbonnen(pbs || []);
      } catch { setVerwantePakbonnen([]); }
    } else setVerwantePakbonnen([]);
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [id]);
  usePakketSync(() => reload());

  // Navigatie naar vorige/volgende factuur in de volgorde van het overzicht (met ↑/↓ toetsen)
  const nav = useDetailNav("facturen", id, "/verkoopfacturen");

  const patch = async (field, value) => {
    try {
      const { data } = await api.patch(`/verkoopfacturen/${id}`, { [field]: value });
      setFactuur(data); toast.success("Opgeslagen");
    } catch { toast.error("Opslaan mislukt"); }
  };

  const remove = async () => {
    try {
      await api.delete(`/verkoopfacturen/${id}`);
      toast.success("Factuur verwijderd");
      navigate("/verkoopfacturen");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verwijderen mislukt");
    }
  };

  const markeerBetaald = async (betaald) => {
    try {
      const { data } = await api.post(`/verkoopfacturen/${id}/betaald`, { betaald });
      setFactuur(data);
      toast.success(betaald ? "Gemarkeerd als betaald" : "Markering ongedaan gemaakt");
    } catch { toast.error("Mislukt"); }
  };

  const removeRegel = async (rid) => {
    await api.delete(`/verkoopfactuurregels/${rid}`);
    toast.success("Regel verwijderd"); reload();
  };

  const totaal = useMemo(() =>
    regels.reduce((s, r) => s + (Number(r.aantal) || 0) * (Number(r.verkoopprijs) || 0), 0)
  , [regels]);

  // Sync bedrag with sum of regels
  useEffect(() => {
    if (factuur && regels.length > 0 && Math.abs((factuur.bedrag || 0) - totaal) > 0.001) {
      patch("bedrag", totaal);
    }
    // eslint-disable-next-line
  }, [totaal]);

  if (!factuur) return <div className="text-muted-foreground">Laden…</div>;

  const isWachtrij = factuur.status_factuur === "nog_te_factureren";
  const isBetaald = factuur.status_factuur === "betaald";
  const isDeels = factuur.status_factuur === "deels_betaald";
  const overdue = factuur.status_factuur === "gefactureerd" && factuur.vervaldatum && new Date(factuur.vervaldatum) < new Date();
  const factuurTotaal = totaal || factuur.bedrag || 0;
  const ontvangen = factuur.bedrag_betaald || 0;
  const openstaand = Math.max(0, factuurTotaal - ontvangen);

  return (
    <div className="space-y-6" data-testid="factuur-detail">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => navigate(nav?.terug || "/verkoopfacturen")} data-testid="back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Overzicht
          </Button>
          {nav?.positie != null && (
            <div className="flex items-center gap-1 border-l border-border pl-2">
              <Button variant="outline" size="sm" disabled={!nav?.vorige}
                onClick={() => nav?.vorige && navigate(`/verkoopfacturen/${nav.vorige}`)}
                title="Vorige factuur in overzicht"
                data-testid="factuur-nav-vorige">
                <ChevronLeft className="h-4 w-4 mr-1" /> Vorige
              </Button>
              <span className="text-xs text-muted-foreground px-1 whitespace-nowrap" data-testid="factuur-nav-positie">
                {nav.positie} / {nav.totaal}
              </span>
              <Button variant="outline" size="sm" disabled={!nav?.volgende}
                onClick={() => nav?.volgende && navigate(`/verkoopfacturen/${nav.volgende}`)}
                title="Volgende factuur in overzicht"
                data-testid="factuur-nav-volgende">
                Volgende <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm"
             onClick={() => taalKeuze.start(org?.land, (taal) => downloadFile(`/verkoopfacturen/${id}/pdf?taal=${taal}`, `factuur-${id.slice(0,8)}.pdf`))}
             data-testid="factuur-pdf-link">
            <FileDown className="h-4 w-4 mr-1" /> Download PDF
          </Button>
          {isWachtrij && (
            <Button size="sm" onClick={()=>setVerstuurOpen(true)} data-testid="open-verstuur">
              <Mail className="h-4 w-4 mr-1" /> Verstuur
            </Button>
          )}
          {!isWachtrij && (
            <Button variant="outline" size="sm" onClick={() => taalKeuze.start(org?.land, (taal) => emailFactuur(taal))} disabled={mailing} data-testid="factuur-email-resend">
              <Mail className="h-4 w-4 mr-1" /> {mailing ? "Mailen…" : "E-mail (PDF)"}
            </Button>
          )}
          {!isWachtrij && factuur.type !== "creditfactuur" && (
            isBetaald ? (
              <Button variant="outline" size="sm" onClick={() => markeerBetaald(false)} data-testid="factuur-onbetaald">
                <RotateCcw className="h-4 w-4 mr-1" /> Markeer als onbetaald
              </Button>
            ) : (
              <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white" onClick={() => markeerBetaald(true)} data-testid="factuur-betaald">
                <CheckCircle2 className="h-4 w-4 mr-1" /> Markeer als betaald
              </Button>
            )
          )}
          {factuur.type !== "creditfactuur" && (
            <Button variant="outline" size="sm"
              onClick={() => setCreditOpen(true)}
              className="border-purple-300 text-purple-700 hover:bg-purple-50 hover:text-purple-800"
              data-testid="factuur-crediteer">
              <AlertTriangle className="h-4 w-4 mr-1" /> Maak creditfactuur
            </Button>
          )}
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={() => setDelOpen(true)} className="text-destructive hover:text-destructive" data-testid="factuur-delete">
              <Trash2 className="h-4 w-4 mr-1" />Verwijderen
            </Button>
          )}
          <MoneybirdKnop factuur={factuur} onSynced={(mb) => setFactuur({...factuur, moneybird: mb})} />
        </div>
      </div>

      <ConfirmDeleteDialog
        open={delOpen}
        onOpenChange={setDelOpen}
        title="Factuur verwijderen?"
        description={`Je staat op het punt factuur ${factuur.factuurnummer || "(concept)"} definitief te verwijderen. Dit kan niet ongedaan worden gemaakt.`}
        onConfirm={remove}
        testid="factuur-delete-dialog"
      />

      <CrediteerDialog
        factuur={factuur}
        regels={regels}
        open={creditOpen}
        onOpenChange={setCreditOpen}
        onCreated={(cid) => { toast.success("Ga naar de nieuwe creditfactuur…"); navigate(`/verkoopfacturen/${cid}`); }}
      />

      {factuur.type === "creditfactuur" && factuur.bron_factuur_id && (
        <div className="rounded-lg border border-purple-200 bg-purple-50 px-4 py-3 text-sm text-purple-900 flex items-center gap-2" data-testid="credit-bron-link">
          <AlertTriangle className="h-4 w-4" />
          <span>Deze creditfactuur is gebaseerd op factuur </span>
          <Link to={`/verkoopfacturen/${factuur.bron_factuur_id}`} className="font-medium underline underline-offset-2 hover:text-purple-950">
            {factuur.bron_factuurnummer || factuur.bron_factuur_id.slice(0, 8)}
          </Link>
          {factuur.credit_reden && <span className="ml-2 text-purple-700">· {factuur.credit_reden}</span>}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-card border border-border rounded-xl p-6 space-y-4">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="text-sm text-muted-foreground">
                  {factuur.type === "creditfactuur" ? "Creditfactuur" : "Verkoopfactuur"}
                </div>
                <Input
                  value={factuur.factuurnummer || ""}
                  onChange={(e)=>setFactuur({...factuur, factuurnummer: e.target.value})}
                  onBlur={(e)=>patch("factuurnummer", e.target.value)}
                  placeholder="(nog niet toegekend)"
                  className="font-display text-3xl font-medium tracking-tight border-0 px-0 h-auto py-1 focus-visible:ring-0 focus-visible:border-0"
                  data-testid="factuur-nummer"
                />
                {org && (
                  <Link to={`/organisaties/${org.id}`} className="inline-flex items-center gap-1 text-sm text-primary hover:underline">
                    {org.naam} <ExternalLink className="h-3 w-3" />
                  </Link>
                )}
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  {isWachtrij ? <Badge variant="secondary">Wachtrij</Badge>
                    : factuur.type === "creditfactuur" ? (factuur.status_factuur === "verwerkt"
                        ? <Badge className="bg-rose-600 text-white hover:bg-rose-600" data-testid="credit-verwerkt-badge"><CheckCircle2 className="h-3 w-3 mr-1" />Creditfactuur · verwerkt</Badge>
                        : <Badge variant="destructive">Creditfactuur</Badge>)
                    : isBetaald ? <Badge className="bg-emerald-500 text-white hover:bg-emerald-500"><CheckCircle2 className="h-3 w-3 mr-1" />Betaald</Badge>
                    : isDeels ? <Badge className="bg-amber-500 text-white hover:bg-amber-500" data-testid="factuur-deels-badge">Deels betaald · {fmtEUR(openstaand)} open</Badge>
                    : overdue ? <Badge className="bg-destructive text-destructive-foreground"><AlertTriangle className="h-3 w-3 mr-1" />Vervallen</Badge>
                    : <Badge>Gefactureerd</Badge>}
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase text-muted-foreground tracking-wide">Totaal</div>
                <div className="font-display text-3xl font-medium" data-testid="factuur-totaal">{fmtEURSigned(totaal || factuur.bedrag || 0, factuur.type === "creditfactuur")}</div>
                {isDeels && (
                  <div className="mt-2 w-44 ml-auto space-y-1" data-testid="factuur-deels-bedragen">
                    <div className="h-2 w-full rounded-full bg-secondary overflow-hidden">
                      <div className="h-full rounded-full bg-emerald-500 transition-all"
                        style={{ width: `${Math.min(100, factuurTotaal > 0 ? (ontvangen / factuurTotaal) * 100 : 0)}%` }}
                        data-testid="factuur-betaal-balk" />
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-emerald-600">Ontvangen {fmtEUR(ontvangen)}</span>
                      <span className="text-amber-600 font-medium">Open {fmtEUR(openstaand)}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pt-2 border-t border-border">
              <div>
                <Label className="text-xs text-muted-foreground">Type</Label>
                <Select value={factuur.type || "factuur"} onValueChange={(v)=>patch("type", v)}>
                  <SelectTrigger data-testid="factuur-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{["factuur","creditfactuur"].map(t=><SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Factuurdatum</Label>
                <Input type="date" data-testid="factuur-datum"
                  value={toDateInput(factuur.datum)}
                  onChange={(e)=>patch("datum", e.target.value ? new Date(e.target.value).toISOString() : null)} />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Vervaldatum</Label>
                <Input type="date" data-testid="factuur-vervaldatum"
                  value={toDateInput(factuur.vervaldatum)}
                  onChange={(e)=>patch("vervaldatum", e.target.value ? new Date(e.target.value).toISOString() : null)} />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Betaaltermijn (dagen)</Label>
                <Input type="number" data-testid="factuur-termijn"
                  value={factuur.factuurtermijn ?? 30}
                  onChange={(e)=>setFactuur({...factuur, factuurtermijn: e.target.value})}
                  onBlur={(e)=>patch("factuurtermijn", parseInt(e.target.value) || 0)} />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Bedrag (€)</Label>
                <Input type="number" step="0.01" value={factuur.bedrag || 0}
                  onChange={(e)=>setFactuur({...factuur, bedrag: e.target.value})}
                  onBlur={(e)=>patch("bedrag", parseFloat(String(e.target.value).replace(",", ".")) || 0)} />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Ordernummer</Label>
                <Input value={factuur.ordernummer || ""}
                  onChange={(e)=>setFactuur({...factuur, ordernummer: e.target.value})}
                  onBlur={(e)=>patch("ordernummer", e.target.value)}
                  data-testid="factuur-ordernummer" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Leveranciers-ID</Label>
                <Input value={factuur.leveranciers_id || ""}
                  onChange={(e)=>setFactuur({...factuur, leveranciers_id: e.target.value})}
                  onBlur={(e)=>patch("leveranciers_id", e.target.value)}
                  data-testid="factuur-levid" />
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-xl">
            <div className="p-4 flex items-center justify-between border-b border-border">
              <div>
                <div className="font-medium">Regels ({regels.length})</div>
                <div className="text-xs text-muted-foreground">Totaal: {fmtEUR(totaal)}</div>
              </div>
              <RegelDialog parentId={id} onSaved={reload}
                trigger={<Button size="sm" data-testid="add-freg-btn"><Plus className="h-4 w-4 mr-1" />Regel toevoegen</Button>} />
            </div>
            {regels.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground text-sm">Nog geen regels.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-muted-foreground bg-secondary/40">
                  <tr>
                    <th className="text-left p-3">Code</th>
                    <th className="text-left p-3">Product</th>
                    <th className="text-right p-3">Aantal</th>
                    <th className="text-right p-3">Verkoop</th>
                    <th className="text-right p-3">Totaal</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {regels.map(r => (
                    <tr key={r.id} data-testid={`fregel-${r.id}`}>
                      <td className="p-3 font-mono text-xs">{r.code}</td>
                      <td className="p-3">{r.product}</td>
                      <td className="p-3 text-right">{r.aantal}</td>
                      <td className="p-3 text-right">{fmtEUR(r.verkoopprijs)}</td>
                      <td className="p-3 text-right font-medium">{fmtEUR((r.aantal||0)*(r.verkoopprijs||0))}</td>
                      <td className="p-3 text-right whitespace-nowrap">
                        <RegelDialog parentId={id} value={r} onSaved={reload}
                          trigger={<Button size="icon" variant="ghost" className="h-7 w-7"><Pencil className="h-3 w-3" /></Button>} />
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={()=>removeRegel(r.id)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="space-y-4">
          {btw && (
            <div className="bg-card border border-border rounded-xl p-5">
              <BtwOverzicht result={btw} />
            </div>
          )}
          <div className="bg-card border border-border rounded-xl p-5 space-y-4" data-testid="factuur-automatisering">
            <div className="text-xs uppercase text-muted-foreground tracking-wide">Automatisering</div>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium">Auto-verzenden bij aflevering</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  Verstuurt de factuur automatisch zodra de bijbehorende MyParcel-zending is bezorgd.
                </div>
              </div>
              <Switch
                checked={factuur.auto_verzenden_op_aflevering !== false}
                onCheckedChange={(v) => patch("auto_verzenden_op_aflevering", v)}
                disabled={factuur.status_factuur !== "concept" && factuur.status_factuur !== "nog_te_factureren"}
                data-testid="factuur-auto-verzenden"
              />
            </div>
            <div className="flex items-start justify-between gap-3 pt-3 border-t border-border">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium">On hold</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  Blokkeert álle automatische én handmatige verzending. Zet dit aan als je een factuur nog nakijkt of bewust wilt vasthouden.
                </div>
              </div>
              <Switch
                checked={factuur.on_hold === true}
                onCheckedChange={(v) => patch("on_hold", v)}
                data-testid="factuur-on-hold"
              />
            </div>
            {factuur.auto_verzonden_op_aflevering_op && (
              <div className="text-xs text-emerald-700 pt-2 border-t border-border">
                Automatisch verzonden op {fmtDateTime(factuur.auto_verzonden_op_aflevering_op)}
              </div>
            )}
            {verwantePakbonnen.length > 0 && (
              <div className="pt-3 border-t border-border">
                <div className="text-xs uppercase text-muted-foreground tracking-wide mb-2">Zending-status</div>
                <div className="space-y-2">
                  {verwantePakbonnen.map(pb => (
                    <div key={pb.id} className="flex items-center justify-between text-sm gap-2">
                      <span className="font-medium truncate">{pb.nummer}</span>
                      <PakketStatusBadge status={pb.pakket_status}
                                          omschrijving={pb.pakket_status_omschrijving}
                                          tracktraceUrl={pb.tracktrace_url} size="xs" />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="bg-card border border-border rounded-xl p-5 space-y-3">
            <div className="text-xs uppercase text-muted-foreground tracking-wide">Meta</div>
            <div className="text-sm space-y-1">
              <div><span className="text-muted-foreground">Aangemaakt:</span> {fmtDateTime(factuur.created_at)}</div>
              {factuur.verzonden_op && <div><span className="text-muted-foreground">Verzonden op:</span> {fmtDateTime(factuur.verzonden_op)}</div>}
              {factuur.betaald_op && <div><span className="text-muted-foreground">Betaald op:</span> {fmtDateTime(factuur.betaald_op)}</div>}
            </div>
            {factuur.verkoopkans_id && (
              <Link to={`/verkoopkansen/${factuur.verkoopkans_id}`}
                    className="inline-flex items-center gap-1 text-sm text-primary hover:underline pt-2 border-t border-border w-full">
                Naar bijbehorende verkoopkans <ExternalLink className="h-3 w-3" />
              </Link>
            )}
            {factuur.pakbon_id && (
              <Link to="/pakbonnen" className="inline-flex items-center gap-1 text-sm text-primary hover:underline">
                Naar pakbonnen <ExternalLink className="h-3 w-3" />
              </Link>
            )}
          </div>
        </div>
      </div>

      <VerstuurDialog factuur={factuur} open={verstuurOpen} onOpenChange={setVerstuurOpen} onSent={reload} />
      <TaalKeuzeDialog open={taalKeuze.open} onOpenChange={taalKeuze.setOpen} land={taalKeuze.land} onKies={taalKeuze.bevestig} titel="Kies de taal van de factuur" />
    </div>
  );
}
